'use client';
import { hideModal, showToast } from '@/redux/navigationSlice';
import { RootState } from '@/redux/store';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { backofficeErrorMessages, rejectProspect } from '@repo/backoffice-api';
import { Doodles } from '@repo/ui';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const DismissProspectBody = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { prospectId, legalName } = useSelector((state: RootState) => state.prospectData);
  const [loading, setLoading] = useState(false);

  const getErrorMessage = (errorCode: string): string => {
    const errorMsg = backofficeErrorMessages[errorCode];
    if (!errorCode || !errorMsg) return 'Hubo un error al desestimar el prospecto';
    return errorMsg;
  };

  const handleDismissProspect = async () => {
    let notAuth = false;
    try {
      if (!prospectId) {
        dispatch(showToast({ text: 'Los datos del prospecto a desestimar no son válidos', kind: 'error' }));
        throw new Error('Invalid prospectId');
      }
      setLoading(true);
      const response = await rejectProspect({ id: prospectId });
      if (response?.notAuth) {
        notAuth = true;
        dispatch(showToast({ text: 'Usuario no autenticado', kind: 'error' }));
        throw new Error('User not authenticated');
      }
      if (response?.softError || response?.hardError) {
        dispatch(showToast({ text: getErrorMessage(response?.message), kind: 'error' }));
        console.error(response);
      }
      dispatch(
        showToast({
          text: `Prospecto ${legalName} desestimado exitosamente`,
          kind: 'success',
        }),
      );
      router.refresh();
    } catch (error) {
      console.error(error);
    } finally {
      if (notAuth) {
        router.push('/login');
        router.refresh();
      }
      setLoading(false);
      dispatch(hideModal());
    }
  };

  return (
    <>
      <Doodles type="confirmation" />
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'error.main',
          bgcolor: 'rgba(211,47,47,0.06)',
          color: 'error.main',
          p: 2,
          borderRadius: 1,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          mt: 2,
        }}
      >
        <Typography variant="body2">¡Atención! Una vez desestimado no podrás continuar con la gestión.</Typography>
      </Box>
      <Stack direction="row" gap={2} width="100%" mt={2}>
        <Button fullWidth variant="outlined" onClick={() => dispatch(hideModal())}>
          Cancelar
        </Button>
        <Button
          fullWidth
          disabled={loading}
          onClick={(e) => {
            e.preventDefault();
            handleDismissProspect();
          }}
        >
          {loading ? (
            <Box sx={{ width: '100%', alignItems: 'center' }}>
              <CircularProgress color="inherit" size={20} />
            </Box>
          ) : (
            <span>Confirmar</span>
          )}
        </Button>
      </Stack>
    </>
  );
};
